import { useState, useCallback, useEffect, useRef } from "react";
import { T, Badge, Card, Btn, DataTable, StatCard, fmtDateTime, font, mono, ST, Inp, Sel } from '../theme.js';
import { api } from '../api-client.js';
import { getToken } from '../auth.js';

// ============================================================
// SCV MODULE — Single Customer View Management
// ============================================================

const SCV_NAV = [
  { id: 'generate', label: 'Generate' },
  { id: 'dq', label: 'DQ Report' },
  { id: 'history', label: 'History' },
  { id: 'codes', label: 'Status Codes' },
  { id: 'links', label: 'Depositor Links' },
];

const RAG = ({ rating }) => {
  const m = { GREEN: 'green', AMBER: 'amber', RED: 'red' };
  return <Badge color={m[rating] || 'gray'}>{rating || 'N/A'}</Badge>;
};

const BASE = window.location.hostname === 'localhost' ? 'http://localhost:3010/api' : '/api';

async function downloadFile(path, filename) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function SCVModule() {
  const [view, setView] = useState('generate');
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);
  const [dqGenId, setDqGenId] = useState(null);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastRef.current) clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const openDqReport = useCallback((genId) => { setDqGenId(genId); setView('dq'); }, []);

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 1000, padding: '10px 16px',
          background: toast.type === 'error' ? T.redBg : toast.type === 'warn' ? T.amberBg : T.greenBg,
          border: `1px solid ${toast.type === 'error' ? T.red : toast.type === 'warn' ? T.amber : T.green}40`,
          borderRadius: 6, fontSize: 12, maxWidth: 480, fontFamily: font }}>
          <span style={{ color: toast.type === 'error' ? T.red : toast.type === 'warn' ? T.amber : T.green, marginRight: 8 }}>
            {toast.type === 'error' ? '\u2717' : toast.type === 'warn' ? '\u26A0' : '\u2713'}
          </span>{toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${T.border}`, overflowX: 'auto' }}>
        {SCV_NAV.map(n => (
          <button key={n.id} onClick={() => setView(n.id)} style={{
            padding: '8px 14px', fontSize: 12, fontWeight: view === n.id ? 600 : 400,
            color: view === n.id ? T.blue : T.textSecondary,
            background: 'transparent', border: 'none',
            borderBottom: view === n.id ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1, whiteSpace: 'nowrap',
          }}>
            {n.label}
          </button>
        ))}
      </div>

      {view === 'generate' && <GenerateView key={tick} showToast={showToast} refresh={refresh} openDqReport={openDqReport} />}
      {view === 'dq' && <DQReportView key={`dq-${dqGenId}-${tick}`} generationId={dqGenId} showToast={showToast} />}
      {view === 'history' && <HistoryView key={tick} openDqReport={openDqReport} />}
      {view === 'codes' && <StatusCodesView key={tick} showToast={showToast} refresh={refresh} />}
      {view === 'links' && <DepositorLinksView key={tick} showToast={showToast} refresh={refresh} />}
    </div>
  );
}

// ============================================================
// VIEW 1: GENERATE
// ============================================================

export function GenerateView({ showToast, refresh, openDqReport }) {
  const [latest, setLatest] = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get('/scv/generations?limit=1').then(h => {
      if (h && h.length > 0) setLatest(h[0]);
    }).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/scv/generate?validate=true');
      setGenResult(r);
      setLatest(null);
      // Reload latest from history
      const h = await api.get('/scv/generations?limit=1');
      if (h && h.length > 0) setLatest(h[0]);
      refresh();

      const dq = r.dqReport;
      if (dq?.rating === 'RED') showToast('CRITICAL data quality issues found \u2014 review DQ report before submitting', 'error');
      else if (dq?.rating === 'AMBER') showToast('Data quality warnings found \u2014 review recommended', 'warn');
      else showToast('SCV files generated successfully \u2014 all DQ checks passed');
    } catch (e) {
      showToast(e.message || 'Generation failed', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const stats = genResult?.stats;
  const dq = genResult?.dqReport;
  const lastGen = latest;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Last Generation" value={lastGen ? fmtDateTime(lastGen.generated_at) : '\u2014'}
          sub={lastGen ? `${lastGen.total_depositors} depositors, ${lastGen.scv_accounts} accounts` : 'No generations yet'}
          color={lastGen ? T.text : T.textMuted} />
        <Card style={{ flex: 1, minWidth: 140, padding: 14 }}>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>DQ Rating</div>
          <div style={{ fontSize: 18 }}>{lastGen ? <RAG rating={lastGen.dq_rating} /> : <Badge color="gray">N/A</Badge>}</div>
          {lastGen && lastGen.dq_critical > 0 && <div style={{ fontSize: 11, color: T.red, marginTop: 4 }}>{lastGen.dq_critical} critical</div>}
        </Card>
        <Card style={{ flex: 1, minWidth: 140, padding: 14 }}>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>SCV File</div>
          {lastGen ? (
            <>
              <div style={{ fontSize: 12, fontFamily: mono, color: T.text, marginBottom: 8, wordBreak: 'break-all' }}>{lastGen.scv_filename}</div>
              <Btn small onClick={() => downloadFile('/scv/generate/download/scv', lastGen.scv_filename).catch(e => showToast(e.message, 'error'))}>Download SCV</Btn>
            </>
          ) : <div style={{ fontSize: 13, color: T.textMuted }}>\u2014</div>}
        </Card>
        <Card style={{ flex: 1, minWidth: 140, padding: 14 }}>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>Exclusions View</div>
          {lastGen && lastGen.excl_accounts > 0 ? (
            <>
              <div style={{ fontSize: 12, fontFamily: mono, color: T.text, marginBottom: 8, wordBreak: 'break-all' }}>{lastGen.excl_filename}</div>
              <Btn small onClick={() => downloadFile('/scv/generate/download/excl', lastGen.excl_filename).catch(e => showToast(e.message, 'error'))}>Download Excl</Btn>
            </>
          ) : <div style={{ fontSize: 13, color: T.textMuted }}>No exclusions</div>}
        </Card>
      </div>

      {/* Generate button */}
      <div style={{ marginBottom: 20 }}>
        <Btn onClick={generate} disabled={generating}>
          {generating ? 'Generating...' : 'Generate New SCV'}
        </Btn>
        {lastGen && <Btn variant="ghost" onClick={() => openDqReport(lastGen.id)} style={{ marginLeft: 8 }}>View DQ Report</Btn>}
        <Btn variant="secondary" onClick={() => downloadFile('/scv/effectiveness-report', `SCV_Effectiveness_Report_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`).catch(e => showToast(e.message, 'error'))} style={{ marginLeft: 8 }}>
          Effectiveness Report (PDF)
        </Btn>
      </div>

      {/* Result stats */}
      {stats && (
        <Card style={{ marginBottom: 16 }}>
          <ST>Generation Results</ST>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
            <div><span style={{ color: T.textMuted }}>Depositors:</span> <b>{stats.totalDepositors}</b></div>
            <div><span style={{ color: T.textMuted }}>SCV Accounts:</span> <b>{stats.scvAccounts}</b></div>
            <div><span style={{ color: T.textMuted }}>Exclusion Accounts:</span> <b>{stats.exclAccounts}</b></div>
            <div><span style={{ color: T.textMuted }}>Skipped (ineligible):</span> <b>{stats.skippedIneligible}</b></div>
            <div><span style={{ color: T.textMuted }}>Skipped (internal):</span> <b>{stats.skippedInternal}</b></div>
          </div>
          {dq && (
            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: T.textMuted }}>DQ:</span>
              <RAG rating={dq.rating} />
              <span style={{ fontSize: 12 }}>{dq.summary.passedChecks}/{dq.summary.totalChecks} checks passed</span>
              {dq.summary.critical > 0 && <Badge color="red">{dq.summary.critical} critical</Badge>}
              {dq.summary.high > 0 && <Badge color="amber">{dq.summary.high} high</Badge>}
              {dq.summary.medium > 0 && <Badge color="blue">{dq.summary.medium} medium</Badge>}
            </div>
          )}
        </Card>
      )}

      {/* Warnings */}
      {stats?.warnings?.length > 0 && (
        <Card>
          <ST>Warnings ({stats.warnings.length})</ST>
          <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 12, fontFamily: mono, lineHeight: 1.6 }}>
            {stats.warnings.slice(0, 50).map((w, i) => (
              <div key={i} style={{ color: w.startsWith('[CRITICAL]') ? T.red : w.startsWith('[HIGH]') ? T.amber : T.textSecondary }}>{w}</div>
            ))}
            {stats.warnings.length > 50 && <div style={{ color: T.textMuted }}>...and {stats.warnings.length - 50} more</div>}
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// VIEW 2: DQ REPORT
// ============================================================

export function DQReportView({ generationId, showToast }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failOnly, setFailOnly] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        let gid = generationId;
        if (!gid) {
          const h = await api.get('/scv/generations?limit=1');
          if (h && h.length > 0) gid = h[0].id;
        }
        if (gid) {
          const r = await api.get(`/scv/dq-report/${gid}`);
          setReport({ ...r, generationId: gid });
        }
      } catch (e) {
        showToast('Failed to load DQ report: ' + e.message, 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [generationId, showToast]);

  if (loading) return <div style={{ padding: 20, color: T.textMuted, fontSize: 13 }}>Loading DQ report...</div>;
  if (!report) return <div style={{ padding: 20, color: T.textMuted, fontSize: 13 }}>No DQ report available. Generate an SCV first.</div>;

  const { rating, summary, findings, checkResults } = report;
  const ragBg = { GREEN: T.greenBg, AMBER: T.amberBg, RED: T.redBg }[rating] || T.surfaceAlt;
  const ragColor = { GREEN: T.green, AMBER: T.amber, RED: T.red }[rating] || T.textMuted;
  const ragMsg = { GREEN: 'All checks passed', AMBER: `${summary.high} warning(s) found`, RED: `${summary.critical} critical issue(s) found` }[rating] || '';

  const sevOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, INFO: 3 };
  const sorted = [...checkResults].sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9));
  const displayed = failOnly ? sorted.filter(c => !c.passed) : sorted;

  const findingsMap = new Map();
  for (const f of findings) findingsMap.set(f.checkId, f);

  return (
    <div>
      {/* RAG Banner */}
      <div style={{ background: ragBg, border: `1px solid ${ragColor}40`, borderRadius: 8, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: ragColor }}>{rating}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: ragColor }}>{ragMsg}</div>
          <div style={{ fontSize: 12, color: T.textSecondary }}>{summary.passedChecks}/{summary.totalChecks} checks passed</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Btn small variant="secondary" onClick={() => {
            downloadFile(`/scv/dq-report/${report.generationId}/export`, `SCV_DQ_Report.xlsx`)
              .catch(e => showToast(e.message, 'error'));
          }}>Export to Excel</Btn>
        </div>
      </div>

      {/* Summary pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Badge color="red">Critical: {summary.critical}</Badge>
        <Badge color="amber">High: {summary.high}</Badge>
        <Badge color="blue">Medium: {summary.medium}</Badge>
        <Badge color="gray">Info: {summary.info}</Badge>
      </div>

      {/* Filter toggle */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ fontSize: 12, color: T.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={failOnly} onChange={e => setFailOnly(e.target.checked)} />
          Show failures only
        </label>
        <span style={{ fontSize: 11, color: T.textMuted }}>({displayed.length} checks shown)</span>
      </div>

      {/* Check results table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font }}>
          <thead><tr>
            {['Check ID', 'Severity', 'Status', 'Issues', 'Rows'].map((h, i) =>
              <th key={i} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{h}</th>
            )}
          </tr></thead>
          <tbody>
            {displayed.map(c => {
              const finding = findingsMap.get(c.checkId);
              const isExpanded = expanded === c.checkId;
              const sevColor = { CRITICAL: 'red', HIGH: 'amber', MEDIUM: 'blue', INFO: 'gray' }[c.severity] || 'gray';
              return [
                <tr key={c.checkId} onClick={() => finding && setExpanded(isExpanded ? null : c.checkId)}
                  style={{ borderBottom: `1px solid ${T.borderLight}`, cursor: finding ? 'pointer' : 'default', background: isExpanded ? T.surfaceAlt : 'transparent' }}>
                  <td style={{ padding: '6px 8px', fontFamily: mono, fontWeight: 500 }}>{c.checkId}</td>
                  <td style={{ padding: '6px 8px' }}><Badge color={sevColor}>{c.severity}</Badge></td>
                  <td style={{ padding: '6px 8px' }}>{c.passed
                    ? <span style={{ color: T.green, fontWeight: 600 }}>{'\u2713'} Pass</span>
                    : <span style={{ color: T.red, fontWeight: 600 }}>{'\u2717'} Fail</span>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{c.issuesFound}</td>
                  <td style={{ padding: '6px 8px' }}>{c.rowsChecked}</td>
                </tr>,
                isExpanded && finding && (
                  <tr key={`${c.checkId}-detail`}>
                    <td colSpan={5} style={{ padding: '8px 16px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, color: T.text }}>{finding.description}</div>
                      <div style={{ maxHeight: 200, overflow: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                          <thead><tr>
                            {['SCV ID', 'Field', 'Value', 'Details'].map((h, i) =>
                              <th key={i} style={{ textAlign: 'left', padding: '3px 6px', color: T.textMuted, fontSize: 10, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                            )}
                          </tr></thead>
                          <tbody>
                            {(finding.affectedRows || []).slice(0, 20).map((r, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                                <td style={{ padding: '3px 6px', fontFamily: mono }}>{r.scvId}</td>
                                <td style={{ padding: '3px 6px' }}>{r.field}</td>
                                <td style={{ padding: '3px 6px', fontFamily: mono, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.value}</td>
                                <td style={{ padding: '3px 6px', color: T.textSecondary }}>{r.details}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(finding.affectedRows?.length || 0) > 20 && <div style={{ fontSize: 11, color: T.textMuted, padding: '4px 6px' }}>...and {finding.affectedRows.length - 20} more</div>}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// VIEW 3: HISTORY
// ============================================================

export function HistoryView({ openDqReport }) {
  const [gens, setGens] = useState([]);
  useEffect(() => { api.get('/scv/generations?limit=20').then(setGens).catch(() => {}); }, []);

  return (
    <div>
      <ST>Generation History</ST>
      <DataTable
        headers={['Date', 'By', 'Depositors', 'SCV', 'Excl', 'DQ Rating', 'DQ Findings', 'Actions']}
        rows={gens.map(g => [
          fmtDateTime(g.generated_at),
          g.generated_by,
          g.total_depositors,
          g.scv_accounts,
          g.excl_accounts,
          <RAG rating={g.dq_rating} />,
          <span style={{ fontSize: 11 }}>
            {g.dq_critical > 0 && <span style={{ color: T.red, marginRight: 6 }}>C:{g.dq_critical}</span>}
            {g.dq_high > 0 && <span style={{ color: T.amber, marginRight: 6 }}>H:{g.dq_high}</span>}
            {g.dq_medium > 0 && <span style={{ color: T.blue }}>M:{g.dq_medium}</span>}
            {g.dq_critical + g.dq_high + g.dq_medium === 0 && <span style={{ color: T.green }}>{'\u2713'} Clean</span>}
          </span>,
          <Btn small variant="ghost" onClick={() => openDqReport(g.id)}>View DQ</Btn>,
        ])}
        compact
      />
    </div>
  );
}

// ============================================================
// VIEW 4: STATUS CODES
// ============================================================

export function StatusCodesView({ showToast, refresh }) {
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', description: '', category: 'FFSTP', exclusion_type: '', sort_order: 0 });

  useEffect(() => { api.get('/scv/status-codes').then(setCodes).catch(() => {}); }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/scv/status-codes/${editing}`, { ...form, version: codes.find(c => c.code === editing)?.version });
        showToast('Status code updated');
      } else {
        await api.post('/scv/status-codes', form);
        showToast('Status code created');
      }
      const updated = await api.get('/scv/status-codes');
      setCodes(updated);
      setShowForm(false); setEditing(null);
      setForm({ code: '', description: '', category: 'FFSTP', exclusion_type: '', sort_order: 0 });
    } catch (e) {
      showToast(e.message || 'Save failed', 'error');
    }
  };

  const startEdit = (c) => {
    setEditing(c.code);
    setForm({ code: c.code, description: c.description, category: c.category, exclusion_type: c.exclusion_type || '', sort_order: c.sort_order });
    setShowForm(true);
  };

  const catColor = c => ({ FFSTP: 'green', NFFSTP: 'amber', EXCLUSION: 'red' }[c] || 'gray');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <ST>SCV Account Status Codes</ST>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn small variant="secondary" onClick={() => downloadFile('/scv/status-codes/export', 'SCV_Status_Codes.xlsx').catch(e => showToast(e.message, 'error'))}>
            Export for SCV Effectiveness Report
          </Btn>
          <Btn small onClick={() => { setShowForm(true); setEditing(null); setForm({ code: '', description: '', category: 'FFSTP', exclusion_type: '', sort_order: 0 }); }}>
            Add New Code
          </Btn>
        </div>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{editing ? `Edit: ${editing}` : 'New Status Code'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
            <Inp label="Code" value={form.code} onChange={v => setForm(f => ({ ...f, code: v }))} disabled={!!editing} placeholder="e.g. MYCODE" />
            <Inp label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="Description of the status code" />
            <Sel label="Category" value={form.category} onChange={v => setForm(f => ({ ...f, category: v, exclusion_type: v === 'EXCLUSION' ? f.exclusion_type : '' }))}
              options={[{ value: 'FFSTP', label: 'FFSTP' }, { value: 'NFFSTP', label: 'NFFSTP' }, { value: 'EXCLUSION', label: 'EXCLUSION' }]} />
            {form.category === 'EXCLUSION' && (
              <Sel label="Exclusion Type" value={form.exclusion_type} onChange={v => setForm(f => ({ ...f, exclusion_type: v }))}
                options={[{ value: '', label: 'Select...' }, { value: 'HMTS', label: 'HMTS' }, { value: 'LEGDIS', label: 'LEGDIS' }, { value: 'LEGDOR', label: 'LEGDOR' }, { value: 'BEN', label: 'BEN' }]} />
            )}
            <Inp label="Sort Order" value={String(form.sort_order)} onChange={v => setForm(f => ({ ...f, sort_order: parseInt(v) || 0 }))} type="number" />
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Btn small onClick={save}>{editing ? 'Update' : 'Create'}</Btn>
            <Btn small variant="secondary" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Btn>
          </div>
        </Card>
      )}

      <DataTable
        headers={['Code', 'Description', 'Category', 'Exclusion Type', 'Active', 'Actions']}
        rows={codes.map(c => [
          <span style={{ fontFamily: mono, fontWeight: 600 }}>{c.code}</span>,
          <span style={{ fontSize: 12 }}>{c.description}</span>,
          <Badge color={catColor(c.category)}>{c.category}</Badge>,
          c.exclusion_type || '\u2014',
          c.active ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>,
          <Btn small variant="ghost" onClick={() => startEdit(c)}>Edit</Btn>,
        ])}
        compact
      />
    </div>
  );
}

// ============================================================
// VIEW 5: DEPOSITOR LINKS
// ============================================================

export function DepositorLinksView({ showToast, refresh }) {
  const [links, setLinks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ scvDepositorId: '', customerId: '', linkType: 'primary', notes: '' });

  const load = useCallback(() => { api.get('/scv/depositor-links').then(setLinks).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      if (!form.customerId) { showToast('Customer ID is required', 'error'); return; }
      const body = { scvDepositorId: form.scvDepositorId || undefined, customerId: form.customerId, linkType: form.linkType, notes: form.notes || undefined };
      await api.post('/scv/depositor-links', body);
      showToast('Depositor link created');
      setShowForm(false);
      setForm({ scvDepositorId: '', customerId: '', linkType: 'primary', notes: '' });
      load();
    } catch (e) {
      showToast(e.message || 'Create failed', 'error');
    }
  };

  const remove = async (customerId) => {
    if (!window.confirm('Remove this depositor link?')) return;
    try {
      await api.delete(`/scv/depositor-links/${customerId}`);
      showToast('Depositor link removed');
      load();
    } catch (e) {
      showToast(e.message || 'Remove failed', 'error');
    }
  };

  const linkTypeColor = t => ({ primary: 'blue', sole_trader_merge: 'purple', duplicate_merge: 'amber', partnership_member: 'green' }[t] || 'gray');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <ST>SCV Depositor Links</ST>
        <Btn small onClick={() => setShowForm(true)}>Create Link</Btn>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>New Depositor Link</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Inp label="SCV Depositor ID (blank = auto)" value={form.scvDepositorId} onChange={v => setForm(f => ({ ...f, scvDepositorId: v }))} placeholder="999999-000001 or leave blank" />
            <Inp label="Customer ID" value={form.customerId} onChange={v => setForm(f => ({ ...f, customerId: v }))} placeholder="cust_..." />
            <Sel label="Link Type" value={form.linkType} onChange={v => setForm(f => ({ ...f, linkType: v }))}
              options={[{ value: 'primary', label: 'Primary' }, { value: 'sole_trader_merge', label: 'Sole Trader Merge' }, { value: 'duplicate_merge', label: 'Duplicate Merge' }, { value: 'partnership_member', label: 'Partnership Member' }]} />
            <Inp label="Notes" value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Optional notes" />
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <Btn small onClick={create}>Create</Btn>
            <Btn small variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      <DataTable
        headers={['SCV Depositor ID', 'Customer ID', 'Link Type', 'Linked By', 'Linked At', 'Notes', 'Actions']}
        rows={links.map(l => [
          <span style={{ fontFamily: mono }}>{l.scv_depositor_id}</span>,
          <span style={{ fontFamily: mono, fontSize: 11 }}>{l.customer_id}</span>,
          <Badge color={linkTypeColor(l.link_type)}>{l.link_type}</Badge>,
          l.linked_by,
          fmtDateTime(l.linked_at),
          <span style={{ fontSize: 11, color: T.textSecondary }}>{l.notes || '\u2014'}</span>,
          <Btn small variant="danger" onClick={() => remove(l.customer_id)}>Remove</Btn>,
        ])}
        compact
      />
      {links.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>No depositor links configured. Links are only needed for merged depositors (e.g. joint accounts across entities).</div>}
    </div>
  );
}
